import { useEffect, useState } from "react";

/** Optional cookie categories; essential cookies need no consent. */
export type ConsentCategory = "analytics" | "marketing";

export interface CookieConsent {
  version: number;
  analytics: boolean;
  marketing: boolean;
  /** ISO time the player answered. */
  decidedAt: string;
}

// Bump to ask everyone again after the categories or the policy change materially.
const CONSENT_VERSION = 1;
const STORAGE_KEY = "skystake.cookie-consent";
const CHANGE_EVENT = "skystake:cookie-consent";
const OPEN_EVENT = "skystake:cookie-settings";

export const readCookieConsent = (): CookieConsent | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CookieConsent>;
    if (parsed?.version !== CONSENT_VERSION) return null;
    return {
      version: CONSENT_VERSION,
      analytics: parsed.analytics === true,
      marketing: parsed.marketing === true,
      decidedAt: String(parsed.decidedAt ?? ""),
    };
  } catch {
    return null;
  }
};

export const saveCookieConsent = (choice: Pick<CookieConsent, ConsentCategory>): CookieConsent => {
  const consent: CookieConsent = {
    version: CONSENT_VERSION,
    analytics: choice.analytics,
    marketing: choice.marketing,
    decidedAt: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // Storage blocked: the choice holds for this page view only and the banner returns next visit.
  }
  window.dispatchEvent(new CustomEvent<CookieConsent>(CHANGE_EVENT, { detail: consent }));
  return consent;
};

/** Gate for any analytics or marketing script: load it only when this is true. */
export const hasCookieConsent = (category: ConsentCategory): boolean =>
  readCookieConsent()?.[category] === true;

/** Re-opens the banner with the category switches showing (footer link, cookie policy page). */
export const openCookieSettings = () => {
  window.dispatchEvent(new Event(OPEN_EVENT));
};

/** For scripts outside React (lib/gtag.ts) that must follow later changes of the choice. */
export const onCookieConsentChange = (listener: (consent: CookieConsent) => void) => {
  const onChange = (event: Event) => listener((event as CustomEvent<CookieConsent>).detail);
  window.addEventListener(CHANGE_EVENT, onChange);
  return () => window.removeEventListener(CHANGE_EVENT, onChange);
};

export const onCookieSettingsOpen = (listener: () => void) => {
  window.addEventListener(OPEN_EVENT, listener);
  return () => window.removeEventListener(OPEN_EVENT, listener);
};

/** The stored choice, kept in step with later changes. */
export const useCookieConsent = (): CookieConsent | null => {
  const [consent, setConsent] = useState<CookieConsent | null>(() => readCookieConsent());

  useEffect(() => {
    const onChange = (event: Event) => setConsent((event as CustomEvent<CookieConsent>).detail);
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => window.removeEventListener(CHANGE_EVENT, onChange);
  }, []);

  return consent;
};
